/**
 * ShareThisCruiseSheet · Milestone 2 (Share This Cruise Sheet + Invitation Send)
 *
 * Consumes `DES-FAMILY-SHARING-EXPERIENCE` v1.1 §12 (Share This Cruise Flow),
 * §14 (Trust chip), and §20 (UX Copy Library). Delivered against the
 * ratified planning-gate checklist `UX-ACC-FAMILY-SHARING-M2-SHARE-INVITE`.
 *
 * Design intent (guiding rule):
 *   "The first invitation should require the fewest possible decisions."
 *
 * Required inputs: recipient (email address). One primary CTA. Nothing else
 * is a required field. A Trust chip is rendered ambiently to reassure the
 * Family Planner about what the invitee will see.
 *
 * Email UX (Product Office · 2026-08-11):
 *   · Allow natural typing without inline errors on every keystroke.
 *   · Validate on blur or after a brief typing pause (~500 ms).
 *   · Explain any issue in plain, warm language beneath the field.
 *   · Keep the Send button disabled until the address is valid.
 *
 * Invitation Confidence (M2-D acceptance criterion):
 *   · Send button disables while the request is in flight (no double-tap).
 *   · On success: sheet closes, sonner toast, parent refreshes roster.
 *   · On the "already pending" idempotency response, we still close and
 *     toast — the Family Planner leaves confident that their invitation
 *     is in the system.
 *
 * IA v1.2 relocation (2026-08-12) — Portal invariants:
 *   Since the sheet now mounts from inside EditVoyageDrawer (whose outer
 *   element is a <form onSubmit={onSave}>), THREE invariants keep the
 *   Send flow correct. Do NOT remove any of them without a compensating
 *   change:
 *     1. `createPortal(document.body)` on return — moves the sheet's
 *        <form> out of the drawer's DOM subtree so HTML doesn't nest
 *        two forms (would otherwise trigger the drawer's submit).
 *     2. `z-[1300]` on the outer container — must sit above the drawer's
 *        z-[1200] overlay so the sheet is hit-testable.
 *     3. `e.stopPropagation()` inside `onSubmit` — React SyntheticEvents
 *        still bubble through the React tree even after DOM portalling,
 *        so the submit event must be halted here or it will reach the
 *        drawer's onSave handler.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const VALIDATE_PAUSE_MS = 500;

export default function ShareThisCruiseSheet({ cruiseId, cruiseName, open, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailErrorVisible, setEmailErrorVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const inputRef = useRef(null);
  const pauseTimerRef = useRef(null);

  // Reset local state whenever the sheet opens fresh.
  useEffect(() => {
    if (open) {
      setEmail("");
      setEmailTouched(false);
      setEmailErrorVisible(false);
      setSubmitting(false);
      setServerError(null);
      // Focus the email field for immediate typing.
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  const emailValid = useMemo(() => EMAIL_RE.test(email.trim().toLowerCase()), [email]);

  // Calm validation: never flash an error while the user is typing.
  // Show the error only if (a) the field has been blurred, or (b) the
  // user has paused for VALIDATE_PAUSE_MS after typing something.
  const onEmailChange = (e) => {
    setEmail(e.target.value);
    setServerError(null);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    setEmailErrorVisible(false); // hide error while typing
    pauseTimerRef.current = setTimeout(() => {
      if (e.target.value.trim().length > 0 && !EMAIL_RE.test(e.target.value.trim().toLowerCase())) {
        setEmailErrorVisible(true);
      }
    }, VALIDATE_PAUSE_MS);
  };

  const onEmailBlur = () => {
    setEmailTouched(true);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    if (email.trim().length > 0 && !emailValid) setEmailErrorVisible(true);
  };

  const canSend = emailValid && !submitting;

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    // React SyntheticEvents propagate through the React tree, NOT the DOM
    // tree — so even though this <form> is portalled to document.body, the
    // submit event will still bubble up through the React parent chain
    // into EditVoyageDrawer's outer <form onSubmit={onSave}>, triggering
    // a PATCH /api/cruises/{id} and closing the drawer. Stop propagation
    // so the sheet's submit is fully self-contained.
    e?.stopPropagation?.();
    if (!canSend) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const res = await api.post(
        `/api/cruises/${cruiseId}/family-sharing/invitations`,
        { recipient_email: email.trim().toLowerCase() },
      );
      const inv = res.data?.invitation;
      const already = !!res.data?.already_pending;
      // Milestone-2 M2-D · Invitation Confidence:
      // regardless of already_pending, the traveler leaves this
      // interaction confident their invitation is in the system.
      toast(already
        ? `Already invited ${inv?.recipient_display_name || "them"} — still on its way.`
        : `Invitation sent to ${inv?.recipient_display_name || email.trim()}.`);
      onSent?.(inv);
      onClose?.();
    } catch (err) {
      // Server error path — surface warmly inline; no red banner.
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again in a moment.";
      setServerError(String(detail));
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-share-sheet-title"
      data-testid="cc-share-cruise-sheet"
      className="fixed inset-0 z-[1300] flex items-center justify-center"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-hidden="true"
        onClick={() => !submitting && onClose?.()}
        data-testid="cc-share-cruise-backdrop"
        className="absolute inset-0 bg-slate-900/40"
        style={{ backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      />

      {/* Sheet */}
      <div
        className="relative w-full max-w-[520px] mx-4 rounded-3xl bg-white shadow-2xl overflow-hidden cc-fade-up"
        style={{ boxShadow: "0 30px 80px rgba(15,23,42,0.28)" }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-2 flex items-start justify-between">
          <div className="pr-6">
            <div className="cc-overline text-[#1e6de5]">Family Sharing</div>
            <h2
              id="cc-share-sheet-title"
              data-testid="cc-share-cruise-title"
              className="font-display font-bold text-[#0F172A] text-[22px] leading-tight mt-1"
            >
              Share {cruiseName || "this cruise"}
            </h2>
            <p className="text-[13.5px] text-[#475569] mt-2 leading-relaxed">
              They'll see everything you plan, without changing anything.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            aria-label="Close"
            data-testid="cc-share-cruise-close"
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={onSubmit} className="px-6 pb-6 space-y-5">
          <div>
            <label htmlFor="cc-share-recipient" className="text-[11.5px] font-black tracking-[0.14em] uppercase text-[#475569]">
              Invite by email
            </label>
            <input
              ref={inputRef}
              id="cc-share-recipient"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={onEmailChange}
              onBlur={onEmailBlur}
              disabled={submitting}
              data-testid="cc-share-cruise-email"
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[14px] text-[#0F172A] placeholder-slate-400 outline-none focus:border-[#1e6de5] focus:ring-2 focus:ring-[#1e6de5]/15 transition-shadow disabled:bg-slate-50 disabled:text-slate-400"
            />
            {/* Calm inline validation copy — never a red banner. */}
            {emailErrorVisible && email.trim().length > 0 && !emailValid && (
              <div
                data-testid="cc-share-cruise-email-error"
                className="mt-1.5 text-[12px] text-[#B45309]"
              >
                That doesn't look like a valid email address.
              </div>
            )}
          </div>

          {/* Trust chip — outcome-worded, ambient, not a permissions preview.
              Renders unconditionally so the Family Planner is reassured
              throughout the interaction (M2-B acceptance criterion). */}
          <div
            data-testid="cc-share-cruise-trust-chip"
            className="rounded-2xl border border-[#DCEAFB] bg-[#F5FAFF] p-4 flex items-start gap-3"
          >
            <ShieldCheck className="w-4 h-4 text-[#1e6de5] flex-shrink-0 mt-[3px]" strokeWidth={2.25} />
            <div className="text-[12.5px] text-[#334155] leading-relaxed">
              They'll be able to <span className="font-semibold text-[#0F172A]">follow your plans</span> — the itinerary,
              dining, and updates. They won't be able to change anything.
            </div>
          </div>

          {/* Server-side error surface — inline, warm, non-blocking. */}
          {serverError && (
            <div
              data-testid="cc-share-cruise-server-error"
              className="rounded-xl bg-[#FFF7ED] border border-[#FED7AA] px-3 py-2 text-[12.5px] text-[#9A3412]"
            >
              {serverError}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
              data-testid="cc-share-cruise-cancel"
              className="rounded-full px-4 py-2 text-[13px] font-semibold text-[#475569] hover:text-[#0F172A] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSend}
              data-testid="cc-share-cruise-send"
              className="rounded-full px-5 py-2.5 text-[13px] font-bold text-white inline-flex items-center gap-2 transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(135deg, #3ea1ff 0%, #1e6de5 100%)",
                boxShadow: "0 8px 22px rgba(30,109,229,0.35)",
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" strokeWidth={2.25} />
                  Send invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
